/**
 * Schema.org JSON-LD generators and components for rich search results.
 */

export interface SchemaServiceProps {
  name: string;
  description: string;
  priceRange?: string;
  url?: string;
}

export function generateServiceSchema({
  name,
  description,
  priceRange = "$199 - $1299/month",
  url = "https://finendra.com",
}: SchemaServiceProps) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: {
      "@type": "Organization",
      name: "Finendra",
      url: "https://finendra.com",
      logo: "https://finendra.com/logo-512.png",
      sameAs: ["https://finendra.com"],
    },
    areaServed: "US",
    priceRange,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Financial Operations Packages",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Essential Bookkeeping",
            description:
              "Monthly bank & credit card reconciliations, categorized transactions, monthly P&L, balance sheet, and tax-ready books for businesses not needing a full-time bookkeeper",
          },
          price: "199",
          priceCurrency: "USD",
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Growth Financial Operations",
            description:
              "Accrual accounting, monthly close by 5th, board pack, AP bill pay, AR invoicing, and payroll execution",
          },
          price: "499",
          priceCurrency: "USD",
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Scale Financial Operations",
            description:
              "Multi-entity consolidation, ASC 606 rev rec, 10+ state sales tax, controller oversight, and audit prep",
          },
          price: "1299",
          priceCurrency: "USD",
        },
      ],
    },
  };
}

export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    name: "Finendra",
    url: "https://finendra.com",
    logo: "https://finendra.com/logo-512.png",
    image: "https://finendra.com/og/og-default.png",
    description:
      "Fractional US bookkeeping starting from $199/mo and institutional finance operations for startups and growing SMBs: bank recs, month-end close by 5th, and controller oversight.",
    telephone: "+1-800-FINENDRA",
    email: "connect@finendra.com",
    priceRange: "$199 - $1299/month",
    areaServed: [
      { "@type": "Country", name: "United States" },
      { "@type": "Country", name: "Worldwide" },
    ],
    address: {
      "@type": "PostalAddress",
      addressCountry: "US",
    },
  };
}

export function generateFaqSchema(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Component to inject raw JSON-LD into the document body or head.
 */
export function SchemaJsonLd({ data }: { data: Record<string, unknown> }) {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}
